// Product form behaviour, shared by the plain and extended-variants product forms.
//
// Requires `window.product_json` to already carry, in addition to Shopify's
// own `{{ product | json }}` output: `currency_symbol`, `quote_only`,
// `minimum_order_quantity`, `variant_minimum_order_quantities` (map of
// variant id -> override) and `variant_breakpoints` (map of variant id ->
// sorted price-break list). These all come from variant/product metafields,
// which `{{ product | json }}` does not serialize on its own — each product
// form snippet builds them via a small Liquid-templated inline <script>
// before this file runs (see tb-product-form-data.liquid).
//
// Also requires resources/js/qf/tb-dropdown.js (window.QF.TbDropdown) to be
// loaded first — the non-extended quantity control is a dropdown built on
// that shared shell (see bindQuantitySection).
//
// Not ported here (confirmed dead in the current markup, not a regression):
// the legacy Shopify.OptionSelectors/radio-button variant-switching machinery
// and the `variant_changed` event chain it drove. Both product forms now
// switch Colour/Branding Method options via a full page navigation (see
// tb-product-form-variant-options.liquid), so there is no more live,
// non-reloading variant change for that code to react to — the
// server-rendered page already reflects the newly selected variant. Two
// pieces of that old code still mattered on initial load regardless of the
// dead event, and are kept below: findVariantImage (as syncGalleryToActiveVariant,
// using the extended file's already-fixed `.main-gallery` selector — main's
// version targeted `.thumb-gallery` while calling `.slideTo()` on the
// `.main-gallery` swiper instance) and the price-breakpoint row highlight
// from `updateVariantInfo` (as highlightActiveBreakpointRow — the SKU/colour
// re-render it also did is dropped as redundant with what Liquid renders).

class ProductForm {
    constructor(root) {
        this.root = root;
        this.productJson = window.product_json;

        this.bindImplicitSubmitGuard();
        this.bindQuantitySections();
        this.bindQuantityModalFooter();
        this.bindCustomisationUpload();
        this.bindHelpButtons();
        this.bindSwatches();
        this.bindPriceGuideBreakpoints();
        this.highlightActiveBreakpointRow();
        this.syncGalleryToActiveVariant();

        document.addEventListener('vat_change', () => this.onVatChange());
    }

    // ---== Setup ==---

    // Enter in a form field (the quantity input, the customisation text
    // field, the Image/Text radios, the size inputs in the quantity modal)
    // triggers the browser's implicit submission, which "clicks" the form's
    // first submit button — `.quote-link`. In order mode that button is only
    // hidden by CSS, never disabled, so implicit submission skipped the
    // Vizurio "customise first" lock on `.product-add-to-cart` and went
    // straight to add-to-basket. Submitting is left to the submit buttons:
    // Enter just commits the field (a `change`, so the quantity minimum and
    // prices still update) and a submission from a hidden or disabled
    // submit button is dropped.
    bindImplicitSubmitGuard() {
        const form = this.root.querySelector('form');
        if (!form) return;

        const nonCommitTypes = ['submit', 'button', 'reset', 'image', 'file', 'radio', 'checkbox'];

        form.addEventListener('keydown', (e) => {
            if (e.key !== 'Enter' || e.isComposing) return;
            const field = e.target;
            if (!(field instanceof HTMLInputElement) || field.form !== form) return;
            if (['submit', 'button', 'reset', 'image'].includes(field.type)) return;

            e.preventDefault();
            if (!nonCommitTypes.includes(field.type)) {
                field.dispatchEvent(new Event('change', { bubbles: true }));
            }
        });

        // Capture on the root so this runs before bindCustomisationValidation
        // and global_listeners.js's submit handler.
        this.root.addEventListener('submit', (e) => {
            if (e.target !== form) return;
            const submitter = e.submitter;
            if (!submitter) return;
            if (submitter.disabled || submitter.getAttribute('aria-disabled') === 'true' || !submitter.offsetParent) {
                e.preventDefault();
                e.stopImmediatePropagation();
            }
        }, true);
    }

    // Resolves the currently active variant id. Extended mode carries the
    // "true" selection in #extended-id (see tb-product-form-extended-variants.liquid);
    // the plain form has no such element, so this naturally falls back to
    // #product-id (a <select> in the plain form, a hidden <input> in extended).
    getActiveVariantId() {
        const extendedId = document.getElementById('extended-id');
        if (extendedId && extendedId.value) return extendedId.value;

        const masterSelect = document.getElementById('product-id');
        return masterSelect ? masterSelect.value : null;
    }

    getVariantBreakpoints(variantId = this.getActiveVariantId()) {
        return this.productJson?.variant_breakpoints?.[variantId] || null;
    }

    getVariantMinimumOrderQuantity(variantId = this.getActiveVariantId()) {
        const variantMOQ = parseInt(this.productJson?.variant_minimum_order_quantities?.[variantId], 10);
        const productMOQ = parseInt(this.productJson?.minimum_order_quantity, 10);
        const resolvedProductMOQ = !Number.isNaN(productMOQ) && productMOQ > 0 ? productMOQ : 1;

        if (!Number.isNaN(variantMOQ) && variantMOQ > resolvedProductMOQ) return variantMOQ;
        return resolvedProductMOQ;
    }

    getQuantityBreakpoint(quantity, breakpoints = this.getVariantBreakpoints()) {
        if (!breakpoints || !breakpoints.length) return null;

        const sortedBreakpoints = breakpoints.map((b) => b.break_point).sort((a, b) => b - a);
        const currentBreakpoint = sortedBreakpoints.find((bp) => bp <= quantity)
            ?? sortedBreakpoints[sortedBreakpoints.length - 1];

        return breakpoints.find((b) => b.break_point == currentBreakpoint) || breakpoints[0];
    }

    // Percentage saved at this breakpoint relative to the lowest (baseline)
    // breakpoint's price — same formula as the Liquid-rendered rows in
    // tb-product-form-quantity.liquid, kept in sync so the trigger label can
    // recompute it live as the quantity changes.
    getBreakpointSavingPercent(breakpoint, variantId = this.getActiveVariantId()) {
        if (!breakpoint) return 0;
        const breakpoints = this.getVariantBreakpoints(variantId);
        const baseline = breakpoints?.[0]?.price;
        if (!baseline) return 0;
        return Math.round(((baseline - breakpoint.price) * 100) / baseline);
    }

    // ---== Quantity (dropdown trigger + free-text input + breakpoint rows,
    // or the extended quantity-modal's display-only footer) ==---
    //
    // Binds every `.form-section.quantity-section` under the root, not just the
    // first — extended mode's size/quantity modal renders its own quantity-footer
    // (a display-only price summary with no #quantity input), so each section is
    // handled defensively rather than assuming the plain form's full structure.

    bindQuantitySections() {
        this.root.querySelectorAll('.form-section.quantity-section').forEach((section) => {
            this.bindQuantitySection(section);
        });
    }

    bindQuantitySection(section) {
        const quantity = section.querySelector('#quantity');
        const quantityFooter = section.querySelector('.quantity-footer');
        const dropdownRoot = section.querySelector('.tb-dropdown[data-tb-quantity]');

        const setFooterPrices = () => this.setQuantityFooterPrices(section, quantity, quantityFooter);
        const enforceMinimum = () => this.enforceQuantityMinimum(quantity);

        if (quantity) {
            quantity.min = this.getEffectiveMinimumQuantity();
        }

        if (quantity && dropdownRoot) {
            const trigger = dropdownRoot.querySelector('.tb-dropdown__trigger');
            const triggerLabel = trigger?.querySelector('.tb-dropdown__trigger-label');
            const symbol = this.productJson?.currency_symbol || '';
            const saveLabel = this.productJson?.strings?.save_percent || 'Save __PERCENT__%';

            const updateTrigger = () => {
                if (!triggerLabel) return;
                const breakpoint = this.getQuantityBreakpoint(quantity.value);
                if (!breakpoint) return;

                const vatToggle = localStorage.getItem('tb_inc_vat') === 'true';
                const unitPrice = (vatToggle ? breakpoint.price * 1.2 : breakpoint.price).toFixed(2);
                const savingPct = this.getBreakpointSavingPercent(breakpoint);

                triggerLabel.innerHTML = `<span class="tb-dropdown__row-qty">${quantity.value}</span>`
                    + (savingPct > 0 ? `<span class="tb-dropdown__row-save">${saveLabel.replace('__PERCENT__', savingPct)}</span>` : '')
                    + `<span class="tb-dropdown__row-price">${symbol}${unitPrice}</span>`;
            };

            const updateRowSelection = () => {
                dropdownRoot.querySelectorAll('[data-dropdown-row]').forEach((row) => {
                    row.setAttribute('aria-selected', row.getAttribute('data-value') == quantity.value ? 'true' : 'false');
                });
            };

            new window.QF.TbDropdown(dropdownRoot, {
                onSelectRow: (row) => {
                    quantity.value = row.getAttribute('data-value');
                    quantity.dispatchEvent(new Event('change', { bubbles: true }));
                },
            });

            quantity.addEventListener('input', () => {
                setFooterPrices();
                this.setQuantityButtonsActive(quantity.value);
                updateTrigger();
                updateRowSelection();
            });

            quantity.addEventListener('change', () => {
                enforceMinimum();
                this.onQuantityChanged(section, quantity, quantityFooter);
                updateTrigger();
                updateRowSelection();
            });

            const form = quantity.closest('form');
            if (form) {
                form.addEventListener('submit', (e) => {
                    if (enforceMinimum()) {
                        e.preventDefault();
                        this.onQuantityChanged(section, quantity, quantityFooter);
                        updateTrigger();
                        updateRowSelection();
                    }
                });
            }

            updateTrigger();
        }

        setFooterPrices();
    }

    onQuantityChanged(section, quantity, quantityFooter) {
        this.setQuantityFooterPrices(section, quantity, quantityFooter);
        this.setQuantityButtonsActive(quantity.value);
    }

    enforceQuantityMinimum(quantity) {
        if (!quantity) return false;

        const min = parseInt(quantity.min, 10);
        if (!(parseInt(quantity.value, 10) >= min)) {
            quantity.value = min;
            return true;
        }
        return false;
    }

    getEffectiveMinimumQuantity(variantId = this.getActiveVariantId()) {
        const minMOQ = this.getVariantMinimumOrderQuantity(variantId);
        const breakpoints = this.getVariantBreakpoints(variantId);
        if (!breakpoints || !breakpoints.length) return minMOQ;

        const minBreakpoint = Math.min(...breakpoints.map((bp) => parseInt(bp.break_point, 10) || 0));
        return Math.max(minMOQ, minBreakpoint);
    }

    setQuantityFooterPrices(section, quantity, quantityFooter) {
        if (!quantity || !quantityFooter) return;

        const breakpoint = this.getQuantityBreakpoint(quantity.value);
        if (!breakpoint) return;

        const vatToggle = localStorage.getItem('tb_inc_vat') === 'true';
        const unitPrice = (vatToggle ? breakpoint.price * 1.2 : breakpoint.price).toFixed(2);
        const unitsSelected = quantity.value;
        const totalPrice = (unitsSelected * unitPrice).toFixed(2);

        const unitPriceEl = quantityFooter.querySelector('.unit-price');
        const totalPriceEl = quantityFooter.querySelector('.total-price');
        const unitsSelectedEl = quantityFooter.querySelector('.units-selected');
        const symbol = this.productJson?.currency_symbol || '';

        if (unitPriceEl) unitPriceEl.innerHTML = symbol + unitPrice;
        if (unitsSelectedEl) unitsSelectedEl.innerHTML = unitsSelected;
        if (totalPriceEl) totalPriceEl.innerHTML = symbol + totalPrice;
    }

    // For multi-size (extended) products, neither the main form's quantity
    // section nor the size/quantity modal's footer has a #quantity input of
    // its own — both just display a price/quantity summary that has to be
    // computed from the *modal body's* hidden total-quantity input (kept in
    // sync across all size steppers by initQuantityModal(), still inline in
    // tb-product-form-extended-variants.liquid). That input isn't inside a
    // `.form-section`, so bindQuantitySections() never reaches it. This
    // binds both `.quantity-footer` displays (main form + modal) to that
    // input's `change` event, which initQuantityModal() already dispatches
    // on every size stepper/manual-input change.
    //
    // This also fixes a pre-existing bug, not just a gap from Phase 2: the
    // original code's equivalent per-section handler for extended==true
    // unconditionally referenced a `quantity` variable that was never
    // declared in that scope, throwing a ReferenceError on the very first
    // multi-size product page load and silently killing everything after it
    // in that handler — so the main form's footer never actually updated
    // even before this refactor.
    bindQuantityModalFooter() {
        if (!this.productJson?.extended) return;

        const totalQuantityInput = this.root.querySelector('.modal-body.quantity-section input.quantity#quantity');
        if (!totalQuantityInput) return;

        const footers = this.root.querySelectorAll('.quantity-footer');
        if (!footers.length) return;

        const refresh = () => {
            footers.forEach((footer) => this.setQuantityFooterPrices(null, totalQuantityInput, footer));
            // Price guide rows are display-only here, but still show which
            // breakpoint the selected total falls into.
            this.setQuantityButtonsActive(totalQuantityInput.value);
        };
        totalQuantityInput.addEventListener('change', refresh);
        this._quantityModalFooterRefresh = refresh;

        refresh();
    }

    // Scrolls the main gallery swiper to the slide matching the currently
    // active variant, once, on load. Swiper initialises asynchronously, so
    // this is deferred rather than run immediately.
    syncGalleryToActiveVariant() {
        const variantId = this.getActiveVariantId();
        if (!variantId) return;

        setTimeout(() => {
            const swiper = window.pdpGallerySwiper;
            if (!swiper) return;

            document.querySelectorAll('#main-product .gallery-wrapper .main-gallery .swiper-slide').forEach((slide, index) => {
                const slideIds = slide.getAttribute('data-variant-ids');
                if (!slideIds) return;
                if (slideIds.split(',').some((id) => id.trim() === String(variantId))) {
                    swiper.slideTo(index, 500, true);
                }
            });
        }, 800);
    }

    // ---== Price guide modal (tb-product-price-guide-modal.liquid) ==---
    // Every variant's breakpoints; opened from the quantity row's "View Full
    // Quantity Price Guide" link. Lives outside the <form>, so it's looked
    // up on the document rather than under this.root.

    getPriceGuideRoot() {
        return document.querySelector('[data-modal="price-guide"]');
    }

    highlightActiveBreakpointRow() {
        const variantId = this.getActiveVariantId();
        this.getPriceGuideRoot()?.querySelectorAll('.variant-breakpoints').forEach((el) => {
            el.classList.toggle('active', el.getAttribute('data-variant-id') == variantId);
        });
    }

    // Click a breakpoint row to jump the form quantity to it, then close
    // the modal so the updated totals are visible.
    bindPriceGuideBreakpoints() {
        const priceGuide = this.getPriceGuideRoot();
        if (!priceGuide) return;

        // Multi-size products manage quantity per-size via the quantity modal,
        // not by clicking a single breakpoint row — the rows are display-only
        // (`.is-display-only` in the snippet); setQuantityButtonsActive() still
        // highlights the row for the selected total (bindQuantityModalFooter).
        if (this.productJson?.extended) return;

        priceGuide.addEventListener('click', (e) => {
            const target = e.target.closest('.price-breakpoint');
            if (!target) return;

            const breakpointQuantity = target.getAttribute('data-quantity');
            if (!breakpointQuantity) return;

            this.root.querySelectorAll('.form-section.quantity-section').forEach((section) => {
                const quantity = section.querySelector('#quantity');
                if (!quantity) return;

                quantity.value = breakpointQuantity;
                // Dispatching `change` (rather than duplicating its effects here)
                // reuses bindQuantitySection's own change listener, so the
                // trigger label/row-selection/footer/MOQ logic stays in one place.
                quantity.dispatchEvent(new Event('change', { bubbles: true }));
            });

            window.modalController?.hideModal('price-guide');
        });
    }

    setQuantityButtonsActive(quantity) {
        const breakpointButtons = this.getPriceGuideRoot()?.querySelectorAll('.price-breakpoint');
        if (!breakpointButtons) return;

        const currentBreakpoint = this.getQuantityBreakpoint(quantity)?.break_point;
        breakpointButtons.forEach((bpBtn) => {
            bpBtn.classList.toggle('active', currentBreakpoint == bpBtn.getAttribute('data-quantity'));
        });
    }

    setQuantityButtonsVATPrices() {
        const breakpointButtons = this.getPriceGuideRoot()?.querySelectorAll('.price-breakpoint');
        if (!breakpointButtons) return;

        const vatToggle = localStorage.getItem('tb_inc_vat') === 'true';
        const symbol = this.productJson?.currency_symbol || '';

        breakpointButtons.forEach((bpBtn) => {
            const unitPrice = parseFloat(bpBtn.getAttribute('data-price'));
            const quantity = parseFloat(bpBtn.getAttribute('data-quantity'));
            if (Number.isNaN(unitPrice) || Number.isNaN(quantity)) return;

            const displayUnitPrice = (vatToggle ? unitPrice * 1.2 : unitPrice).toFixed(2);
            const displayTotalPrice = (vatToggle ? unitPrice * 1.2 * quantity : unitPrice * quantity).toFixed(2);

            const unitPriceEl = bpBtn.querySelector('.unit-price');
            const totalPriceEl = bpBtn.querySelector('.total-price');
            if (unitPriceEl) unitPriceEl.innerHTML = symbol + displayUnitPrice;
            if (totalPriceEl) totalPriceEl.innerHTML = symbol + displayTotalPrice;
        });
    }

    onVatChange() {
        this.root.querySelectorAll('.form-section.quantity-section').forEach((section) => {
            const quantity = section.querySelector('#quantity');
            const quantityFooter = section.querySelector('.quantity-footer');
            this.setQuantityFooterPrices(section, quantity, quantityFooter);
            // Re-dispatching `input` (without changing the value) reuses the
            // dropdown trigger-label refresh already wired in
            // bindQuantitySection, so the VAT-sensitive unit price shown on
            // the trigger stays correct without duplicating that logic here.
            if (quantity) quantity.dispatchEvent(new Event('input', { bubbles: true }));
        });
        if (this._quantityModalFooterRefresh) this._quantityModalFooterRefresh();
        this.setQuantityButtonsVATPrices();
    }

    // ---== Customisation upload (Image/Text radios) ==---
    //
    // The Image/Text radios (tb-product-form-customisation-upload.liquid)
    // aren't submitted themselves — the choice is mirrored into the
    // "Upload Method" hidden input, and only the active field's inputs carry
    // the `property` class that global_listeners.js / form-quote.liquid
    // serialise into line-item properties.

    bindCustomisationUpload() {
        const customisationUpload = this.root.querySelector('.form-section.customisation-upload');
        if (!customisationUpload) return;

        const typeInputs = customisationUpload.querySelectorAll('input.customisation-type-input');
        const imageInput = customisationUpload.querySelector('#artwork-select');
        const imagePropInput = customisationUpload.querySelector('#selected-artwork-prop');
        const textInput = customisationUpload.querySelector('#text-upload');
        const uploadMethodInput = customisationUpload.querySelector('#upload-method-input');

        const imageUploader = document.querySelector('image-uploader');
        const imageUploaderTrigger = customisationUpload.querySelector('.open-upload-modal');

        const getUploadType = () => customisationUpload.querySelector('input.customisation-type-input:checked')?.value || '';

        const applyUploadType = (uploadType, { userInitiated = false } = {}) => {
            customisationUpload.setAttribute('data-upload-type', uploadType);
            this.clearCustomisationErrors(customisationUpload);

            if (uploadMethodInput) {
                uploadMethodInput.value = uploadType;
                uploadMethodInput.classList.toggle('property', uploadType === 'image' || uploadType === 'text');
            }

            const isText = uploadType === 'text';
            const isImage = uploadType === 'image';

            textInput?.toggleAttribute('required', isText);
            textInput?.classList.toggle('property', isText);
            imageInput?.classList.toggle('property', isImage);
            imagePropInput?.classList.toggle('property', isImage);

            if (!userInitiated) return;

            if (isText) {
                textInput?.focus();
            } else if (isImage && !imageInput?.value) {
                // Deferred: the radio change comes from a click that's still
                // bubbling — ImageUploaderV2 listens for clicks on `document`
                // and closes its modal on any click outside it, so opening
                // synchronously would be undone before the click finishes.
                setTimeout(() => imageUploader?.openModal(), 0);
            }
        };

        typeInputs.forEach((input) => {
            input.addEventListener('change', () => applyUploadType(getUploadType(), { userInitiated: true }));
        });

        // Preselected radio (Image) — set up properties without opening the modal.
        applyUploadType(getUploadType());

        // The trigger is a div[role=button] (ImageUploaderV2 opens on clicks
        // targeting it directly) — give it keyboard activation too.
        imageUploaderTrigger?.addEventListener('keydown', (e) => {
            if (e.key !== 'Enter' && e.key !== ' ') return;
            e.preventDefault();
            imageUploader?.openModal();
        });

        // Any artwork change clears a stale "artwork required" error.
        window.addEventListener('ImageUploader:artwork_selected', () => this.clearCustomisationErrors(customisationUpload));

        this.bindCustomisationValidation(customisationUpload, { imageInput, textInput, getUploadType });

        if (textInput) {
            textInput.addEventListener('input', () => {
                const value = textInput.value;
                const submitBtn = document.querySelector('.product-add-to-cart');
                const emojiMsg = document.querySelector('.emoji-message');
                const hasEmoji = !!(value && value.match(/(©|®|[ -㌀]|\ud83c[퀀-\udfff]|\ud83d[퀀-\udfff]|\ud83e[퀀-\udfff])/g));

                if (emojiMsg) emojiMsg.style.display = hasEmoji ? 'block' : 'none';
                if (submitBtn) {
                    submitBtn.classList.toggle('disabled', hasEmoji);
                    if (hasEmoji) submitBtn.setAttribute('disabled', true);
                    else submitBtn.removeAttribute('disabled');
                }
            });
        }
    }

    // Order path only: block submit until the chosen customisation is
    // actually provided. Quote requests stay optional. Registered in the
    // capture phase on the form's ancestor so it runs before
    // global_listeners.js's own submit handler on the form. Skipped while
    // the upload UI is hidden (Vizurio order path — add-to-basket happens
    // inside the Vizurio builder).
    bindCustomisationValidation(customisationUpload, { imageInput, textInput, getUploadType }) {
        const form = customisationUpload.closest('form');
        if (!form) return;

        this.root.addEventListener('submit', (e) => {
            if (e.target !== form) return;
            if (this.root.getAttribute('data-form-type') !== 'order') return;
            if (!customisationUpload.offsetParent) return;

            const uploadType = getUploadType();
            let missing = null;
            if (uploadType === 'image' && !imageInput?.value) missing = 'image';
            if (uploadType === 'text' && !textInput?.value.trim()) missing = 'text';
            if (!missing) return;

            e.preventDefault();
            e.stopImmediatePropagation();

            customisationUpload.classList.add('has-error');
            const error = customisationUpload.querySelector(`.customisation-upload__error[data-error-for="${missing}"]`);
            if (error) error.hidden = false;

            const focusTarget = missing === 'image' ? customisationUpload.querySelector('.open-upload-modal') : textInput;
            focusTarget?.focus();
            focusTarget?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }, true);

        textInput?.addEventListener('input', () => this.clearCustomisationErrors(customisationUpload));
    }

    clearCustomisationErrors(customisationUpload) {
        customisationUpload.classList.remove('has-error');
        customisationUpload.querySelectorAll('.customisation-upload__error').forEach((el) => { el.hidden = true; });
    }

    // ---== "How can we help" quote/order toggle ==---

    bindHelpButtons() {
        const productFormEl = this.root;
        const helpButtons = this.root.querySelectorAll('.form-section.help-section .help-buttons .help-button');
        const formTypeChangeEvent = new Event('form-type-change');

        helpButtons.forEach((helpButton) => {
            helpButton.addEventListener('click', () => {
                const formType = helpButton.getAttribute('data-form-type');

                helpButtons.forEach((b) => b.classList.remove('active'));
                helpButton.classList.add('active');
                productFormEl.setAttribute('data-form-type', formType);
                document.dispatchEvent(formTypeChangeEvent);

                // Order-path customisation checks run on submit
                // (bindCustomisationValidation); clear any shown when switching.
                const customisationUpload = this.root.querySelector('.form-section.customisation-upload');
                if (customisationUpload) this.clearCustomisationErrors(customisationUpload);
            });
        });
    }

    // ---== Colour swatches: "more colours" collapse/expand ==---

    bindSwatches() {
        const swatchesWrapper = document.querySelector('.product-swatches');
        const swatches = document.querySelectorAll('.product-swatch');
        if (!swatchesWrapper) return;

        const swatchBreakpoints = { 1199.98: 8, 767.98: 6, 0: 4 };

        const getSwatchesToShow = () => {
            if (swatchesWrapper.classList.contains('active-locked')) return;

            const viewportWidth = window.innerWidth;
            const sortedBreakpoints = Object.keys(swatchBreakpoints).sort((a, b) => b - a).map(Number);
            const matchingBreakpoint = sortedBreakpoints.find((bp) => viewportWidth >= bp);
            const swatchesToShow = swatchBreakpoints[matchingBreakpoint];

            let delay = 0;
            swatches.forEach((swatch, i) => {
                if (i >= swatchesToShow - 1) swatch.style.setProperty('--transition-delay', `${delay++ / 10}s`);
                else swatch.style.setProperty('--transition-delay', '0s');
            });

            if (swatches.length >= swatchesToShow) swatchesWrapper.classList.remove('active');
        };
        getSwatchesToShow();

        let swatchResizeTimeout;
        let lastViewportWidth = window.innerWidth;
        window.addEventListener('resize', () => {
            const viewportWidth = window.innerWidth;
            if (viewportWidth !== lastViewportWidth) {
                clearTimeout(swatchResizeTimeout);
                swatchResizeTimeout = setTimeout(getSwatchesToShow, 200);
            }
            lastViewportWidth = viewportWidth;
        });

        document.querySelectorAll('.more-colours').forEach((moreColoursButton) => {
            moreColoursButton.addEventListener('click', () => {
                swatchesWrapper.classList.add('active', 'active-locked');
            });
        });
    }
}

// ---== Info popovers (snippets/tb-popover.liquid) ==---
//
// Used by the "All-Inclusive" price popover (tb-product-form-quantity.liquid)
// and the "Find the best option" (i) popover (tb-product-form-help.liquid).
//
// Desktop: opens on hover/focus via CSS; this adds tap-to-toggle,
// click-outside and Esc, and keeps the panel inside the viewport —
// horizontally via --popover-shift, vertically by flipping it below the
// trigger (`.is-below`) when it won't fit above.
// Mobile (≤1029.98px): CSS turns the panel into a centred modal that only
// opens on tap; this locks page scroll while it's open, moves focus to the
// close button and back to the trigger on close.
//
// Delegated from `document`, so it's bound once per page load (outside
// ProductForm, which is re-created on every `sectionUpdated`) and covers
// re-rendered markup.

function bindTbPopovers() {
    const modalQuery = window.matchMedia('(max-width: 1029.98px)');
    const isModal = () => modalQuery.matches;
    const gutter = 16;

    // Worked out from the trigger's position and the panel's size rather
    // than the panel's own rect — the panel's transform animates, so its
    // rect mid-transition would include the previous shift.
    const positionPanel = (popover) => {
        const panel = popover?.querySelector('.tb-popover__panel');
        if (!panel || isModal()) return;

        const anchor = popover.getBoundingClientRect();
        const width = panel.offsetWidth;
        const left = anchor.left + (anchor.width / 2) - (width / 2);
        const right = left + width;
        const viewportWidth = document.documentElement.clientWidth;
        let shift = 0;
        if (left < gutter) shift = gutter - left;
        else if (right > viewportWidth - gutter) shift = (viewportWidth - gutter) - right;
        panel.style.setProperty('--popover-shift', `${Math.round(shift)}px`);

        // The sticky mega menu covers the top of the viewport
        const stickyHeader = document.querySelector('.mega-menu-outer-shopify-wrapper');
        const topEdge = Math.max(stickyHeader?.getBoundingClientRect().bottom || 0, 0) + gutter;
        const gap = parseFloat(getComputedStyle(popover).getPropertyValue('--tb-popover-gap')) || 14;
        const needed = panel.offsetHeight + gap;
        const spaceAbove = anchor.top - topEdge;
        const spaceBelow = window.innerHeight - anchor.bottom - gutter;
        popover.classList.toggle('is-below', spaceAbove < needed && spaceBelow > spaceAbove);
    };

    const getOpen = () => document.querySelectorAll('[data-tb-popover].is-open');

    const syncScrollLock = () => {
        document.documentElement.classList.toggle('tb-popover-locked', isModal() && getOpen().length > 0);
    };

    const open = (popover) => {
        const trigger = popover.querySelector('.tb-popover__trigger');
        positionPanel(popover);
        popover.classList.add('is-open');
        trigger?.setAttribute('aria-expanded', 'true');
        syncScrollLock();
        if (isModal()) popover.querySelector('.tb-popover__close')?.focus();
    };

    const close = (popover, { returnFocus = false } = {}) => {
        const trigger = popover.querySelector('.tb-popover__trigger');
        popover.classList.remove('is-open');
        trigger?.setAttribute('aria-expanded', 'false');
        syncScrollLock();
        if (returnFocus && isModal()) trigger?.focus();
    };

    const closeAll = (except) => {
        getOpen().forEach((popover) => {
            if (popover !== except) close(popover);
        });
    };

    ['mouseover', 'focusin'].forEach((type) => {
        document.addEventListener(type, (event) => {
            const popover = event.target.closest?.('[data-tb-popover]');
            if (popover && !popover.contains(event.relatedTarget)) positionPanel(popover);
        });
    });

    document.addEventListener('click', (event) => {
        const popover = event.target.closest('[data-tb-popover]');

        if (!popover) {
            closeAll();
            return;
        }

        if (event.target.closest('.tb-popover__close, .tb-popover__backdrop')) {
            close(popover, { returnFocus: true });
            return;
        }

        if (event.target.closest('.tb-popover__trigger')) {
            closeAll(popover);
            if (popover.classList.contains('is-open')) close(popover);
            else open(popover);
        }
    });

    document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        const focused = document.activeElement?.closest('[data-tb-popover]');
        const wasModal = isModal() && getOpen().length > 0;
        getOpen().forEach((popover) => close(popover, { returnFocus: wasModal && popover === focused }));
        if (focused && !wasModal) {
            focused.classList.add('is-dismissed');
            focused.addEventListener('mouseleave', () => focused.classList.remove('is-dismissed'), { once: true });
            focused.addEventListener('focusout', () => focused.classList.remove('is-dismissed'), { once: true });
        }
    });

    // Crossing the breakpoint swaps popover <-> modal; start closed.
    modalQuery.addEventListener('change', () => closeAll());
}

// ---== Spec table "Prices from" VAT swap (tb-product-spec-table.liquid) ==---
//
// The row sits in the product-information section, outside any
// ProductForm root, so it's handled at document level. vat-toggle.js fires
// `vat_change` on load and on every Inc/Exc toggle.

function bindSpecTableVatPrices() {
    document.addEventListener('vat_change', () => {
        const incVat = localStorage.getItem('tb_inc_vat') === 'true';
        document.querySelectorAll('.spec-table [data-exc-vat-price][data-inc-vat-price]').forEach((el) => {
            el.textContent = el.getAttribute(incVat ? 'data-inc-vat-price' : 'data-exc-vat-price');
        });
    });
}

function initProductForms() {
    document.querySelectorAll('.product-form').forEach((el) => new ProductForm(el));
}

bindTbPopovers();
bindSpecTableVatPrices();
document.addEventListener('DOMContentLoaded', initProductForms);
document.addEventListener('sectionUpdated', initProductForms);

window.QF = window.QF || {};
window.QF.ProductForm = ProductForm;
